import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, EMPTY, from, switchMap } from 'rxjs';
import { AuthService } from '../auth/auth.service';

const GRAPH_USER_URL = 'https://graph.microsoft.com/v1.0/users/';
/** Delegated and already consented on the app registration; the narrower User.ReadBasic.All is not on it. */
export const PERSON_NAME_SCOPE = 'User.Read.All';

/** What shows for a person whose name cannot be looked up. */
export function oidAlias(oid: string): string {
  return `User ${oid.slice(0, 8)}`;
}

/**
 * Display names for the Entra object ids the workflow engine records as owners and actors.
 *
 * Names come from Microsoft Graph with the delegated scope User.Read.All, which the app registration has consented. The token
 * is requested silently and the call is made here rather than through the MSAL interceptor: should the scope ever be missing,
 * the interceptor would redirect the browser to ask for it. Without the scope every name is an alias.
 */
@Injectable({ providedIn: 'root' })
export class PersonNameService {
  private _http = inject(HttpClient);
  private _msal = inject(MsalService);
  private _auth = inject(AuthService);

  private _names = signal<Record<string, string>>({});
  private _asked = new Set<string>();
  private _scopeUnavailable = false;

  /** The cached name, or an alias until one is known; null for no id. Reads a signal, so templates update when a name lands. */
  nameOf(oid: string | null | undefined): string | null {
    return oid ? (this._names()[oid] ?? oidAlias(oid)) : null;
  }

  /** Looks up each id not asked for before. */
  request(oids: readonly (string | null | undefined)[]): void {
    for (const oid of new Set(oids)) {
      if (!oid || this._asked.has(oid)) {
        continue;
      }
      this._asked.add(oid);

      const ownName = oid === this._auth.getObjectId() ? this._auth.getAccount()?.name : undefined;
      if (ownName) {
        this._remember(oid, ownName);
      } else {
        this._lookup(oid);
      }
    }
  }

  private _lookup(oid: string): void {
    const account = this._auth.getAccount();
    if (this._scopeUnavailable || !account) {
      return;
    }

    from(this._msal.instance.acquireTokenSilent({ scopes: [PERSON_NAME_SCOPE], account }))
      .pipe(
        catchError(() => {
          this._scopeUnavailable = true;
          return EMPTY;
        }),
        switchMap(token =>
          this._http.get<{ displayName?: string | null }>(`${GRAPH_USER_URL}${encodeURIComponent(oid)}?$select=displayName`, {
            headers: new HttpHeaders({ Authorization: `Bearer ${token.accessToken}` }),
          }),
        ),
        catchError(() => EMPTY),
      )
      .subscribe(user => {
        if (user.displayName) {
          this._remember(oid, user.displayName);
        }
      });
  }

  private _remember(oid: string, name: string): void {
    this._names.update(names => ({ ...names, [oid]: name }));
  }
}
